import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  Alert,
  FormControlLabel,
  Checkbox,
  IconButton,
  Paper,
  Stack,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Article as ArticleIcon,
  Gavel as GavelIcon,
  Security as SecurityIcon,
  Person as PersonIcon,
  Payment as PaymentIcon,
  Report as ReportIcon,
  Copyright as CopyrightIcon,
} from '@mui/icons-material';

const TermsOfServiceDialog = ({ open, onClose, onAccept, isAccepted = false }) => {
  const [accepted, setAccepted] = useState(false);
  const [readSections, setReadSections] = useState(new Set());
  const theme = useTheme();
  
  // Update accepted state when isAccepted prop changes
  useEffect(() => {
    setAccepted(isAccepted);
  }, [isAccepted]);
  
  // Refs for scrolling to sections
  const agreementRef = useRef(null);
  const accountsRef = useRef(null);
  const contentRef = useRef(null);
  const paymentsRef = useRef(null);
  const terminationRef = useRef(null);
  const liabilityRef = useRef(null);
  const generalRef = useRef(null);

  const scrollToSection = (ref, sectionName) => {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setReadSections(prev => new Set([...prev, sectionName]));
  };

  const handleAccept = () => {
    const newState = !isAccepted;
    if (onAccept) onAccept(newState);
    onClose();
  };

  const markAllAsRead = () => {
    const allSections = ['agreement', 'accounts', 'content', 'payments', 'termination', 'liability', 'general'];
    setReadSections(new Set(allSections));
    setAccepted(true);
  };

  const isFullyRead = readSections.size >= 7;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth sx={{ zIndex: 3000 }} PaperProps={{ sx: { borderRadius: 2, height: '90vh', maxHeight: 800 } }}>
      {/* Header */}
      <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider', py: 2, pr: 8 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ArticleIcon color="primary" />
          <Box>
            <Typography variant="h5" fontWeight={600}>Terms of Service</Typography>
            <Typography variant="body2" color="text.secondary">Last updated: June 1st, 2026 | Effective immediately</Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        <Box sx={{ display: 'flex', height: '70vh' }}>
          
          {/* LEFT MENU - Navigation only */}
          <Paper elevation={0} sx={{ 
            width: 260, 
            flexShrink: 0, 
            borderRight: 1, 
            borderColor: 'divider', 
            overflow: 'auto',
            bgcolor: alpha(theme.palette.primary.main, 0.02),
          }}>
            <Box sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Table of Contents
              </Typography>
              
              <Stack spacing={0.5} sx={{ mt: 2 }}>
                <Button
                  startIcon={<GavelIcon fontSize="small" />}
                  onClick={() => scrollToSection(agreementRef, 'agreement')}
                  fullWidth
                  sx={{
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    color: 'text.primary',
                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) }
                  }}
                >
                  1. Agreement
                  {readSections.has('agreement') && <CheckCircleIcon fontSize="small" color="success" sx={{ ml: 'auto' }} />}
                </Button>

                <Button
                  startIcon={<PersonIcon fontSize="small" />}
                  onClick={() => scrollToSection(accountsRef, 'accounts')}
                  fullWidth
                  sx={{
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    color: 'text.primary',
                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) }
                  }}
                >
                  2. User Accounts
                  {readSections.has('accounts') && <CheckCircleIcon fontSize="small" color="success" sx={{ ml: 'auto' }} />}
                </Button>

                <Button
                  startIcon={<CopyrightIcon fontSize="small" />}
                  onClick={() => scrollToSection(contentRef, 'content')}
                  fullWidth
                  sx={{
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    color: 'text.primary',
                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) }
                  }}
                >
                  3. Content
                  {readSections.has('content') && <CheckCircleIcon fontSize="small" color="success" sx={{ ml: 'auto' }} />}
                </Button>

                <Button
                  startIcon={<PaymentIcon fontSize="small" />}
                  onClick={() => scrollToSection(paymentsRef, 'payments')}
                  fullWidth
                  sx={{
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    color: 'text.primary',
                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) }
                  }}
                >
                  4. Payments
                  {readSections.has('payments') && <CheckCircleIcon fontSize="small" color="success" sx={{ ml: 'auto' }} />}
                </Button>

                <Button
                  startIcon={<ReportIcon fontSize="small" />}
                  onClick={() => scrollToSection(terminationRef, 'termination')}
                  fullWidth
                  sx={{
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    color: 'text.primary',
                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) }
                  }}
                >
                  5. Termination
                  {readSections.has('termination') && <CheckCircleIcon fontSize="small" color="success" sx={{ ml: 'auto' }} />}
                </Button>

                <Button
                  startIcon={<SecurityIcon fontSize="small" />}
                  onClick={() => scrollToSection(liabilityRef, 'liability')}
                  fullWidth
                  sx={{
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    color: 'text.primary',
                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) }
                  }}
                >
                  6. Liability
                  {readSections.has('liability') && <CheckCircleIcon fontSize="small" color="success" sx={{ ml: 'auto' }} />}
                </Button>

                <Button
                  startIcon={<ArticleIcon fontSize="small" />}
                  onClick={() => scrollToSection(generalRef, 'general')}
                  fullWidth
                  sx={{
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    color: 'text.primary',
                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) }
                  }}
                >
                  7. General Terms
                  {readSections.has('general') && <CheckCircleIcon fontSize="small" color="success" sx={{ ml: 'auto' }} />}
                </Button>
              </Stack>

              <Divider sx={{ my: 3 }} />

              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Reading Progress
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Sections read: {readSections.size}/7
                </Typography>
                <Box sx={{ 
                  width: '100%', 
                  height: 4, 
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  borderRadius: 2,
                  mt: 1,
                }}>
                  <Box sx={{ 
                    width: `${(readSections.size / 7) * 100}%`, 
                    height: '100%',
                    bgcolor: 'primary.main',
                    borderRadius: 2,
                    transition: 'width 0.3s',
                  }} />
                </Box>
              </Box>
            </Box>
          </Paper>

          {/* RIGHT SIDE - All scrollable content */}
          <Box sx={{ 
            flex: 1, 
            overflow: 'auto', 
            p: 3,
            '&::-webkit-scrollbar': {
              width: 8,
            },
            '&::-webkit-scrollbar-track': {
              bgcolor: alpha(theme.palette.primary.main, 0.05),
              borderRadius: 4,
            },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: alpha(theme.palette.primary.main, 0.3),
              borderRadius: 4,
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.5),
              },
            },
          }}>
            <Alert severity="info" sx={{ mb: 4 }}>
              <Typography variant="body2">
                Please read these terms carefully before using our services. By using our platform, 
                you agree to be bound by these terms.
              </Typography>
            </Alert>

            {/* Section 1 - Agreement */}
            <Box ref={agreementRef} id="agreement" sx={{ scrollMarginTop: 20, mb: 6 }}>
              <Typography variant="h5" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <GavelIcon /> 1. Agreement
              </Typography>
              
              <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 2 }}>
                1.1 Acceptance of Terms
              </Typography>
              <Typography variant="body2" paragraph>
                By accessing and using our platform, you acknowledge that you have read, understood, 
                and agree to be bound by these Terms of Service. If you do not agree with any part 
                of these terms, you must not use our services.
              </Typography>
              
              <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 3 }}>
                1.2 Modification of Terms
              </Typography>
              <Typography variant="body2" paragraph>
                We reserve the right to modify these terms at any time. We will notify users of 
                significant changes via email or platform notifications. Continued use of our 
                services after changes constitutes acceptance of the new terms.
              </Typography>
              
              <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 3 }}>
                1.3 Eligibility
              </Typography>
              <Typography variant="body2" paragraph>
                You must be at least 13 years old to use our services. If you are under 18, you 
                must have parental or guardian consent. By using our services, you represent 
                that you meet these requirements.
              </Typography>
            </Box>

            <Divider sx={{ my: 4 }} />

            {/* Section 2 - User Accounts */}
            <Box ref={accountsRef} id="accounts" sx={{ scrollMarginTop: 20, mb: 6 }}>
              <Typography variant="h5" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon /> 2. User Accounts
              </Typography>
              
              <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 2 }}>
                2.1 Account Registration
              </Typography>
              <Typography variant="body2" paragraph>
                You must provide accurate and complete information when creating an account. 
                You are responsible for maintaining the confidentiality of your account 
                credentials and for all activities that occur under your account.
              </Typography>
              
              <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 3 }}>
                2.2 Account Security
              </Typography>
              <Typography variant="body2" paragraph>
                You must immediately notify us of any unauthorized use of your account or 
                any other security breach. We are not liable for any loss or damage arising 
                from your failure to comply with this security obligation.
              </Typography>
              
              <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 3 }}>
                2.3 Account Termination
              </Typography>
              <Typography variant="body2" paragraph>
                We reserve the right to suspend or terminate your account at our sole discretion 
                for violations of these terms or for any other reason. You may terminate your 
                account at any time by contacting support.
              </Typography>
            </Box>

            <Divider sx={{ my: 4 }} />

            {/* Section 3 - Content */}
            <Box ref={contentRef} id="content" sx={{ scrollMarginTop: 20, mb: 6 }}>
              <Typography variant="h5" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CopyrightIcon /> 3. Content
              </Typography>
              
              <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 2 }}>
                3.1 User Content
              </Typography>
              <Typography variant="body2" paragraph>
                You retain ownership of all content you submit to our platform. By submitting 
                content, you grant us a worldwide, non-exclusive, royalty-free license to use, 
                reproduce, and display such content in connection with our services.
              </Typography>
              
              <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 3 }}>
                3.2 Content Standards
              </Typography>
              <Typography variant="body2" paragraph>
                You agree not to post content that:
              </Typography>
              <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                <Typography component="li" variant="body2" paragraph>Is illegal, harmful, threatening, abusive, harassing, defamatory, or invasive of privacy</Typography>
                <Typography component="li" variant="body2" paragraph>Infringes on intellectual property rights</Typography>
                <Typography component="li" variant="body2" paragraph>Contains viruses or malicious code</Typography>
                <Typography component="li" variant="body2" paragraph>Is spam or commercial solicitation</Typography>
              </Box>
              
              <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 3 }}>
                3.3 Content Moderation
              </Typography>
              <Typography variant="body2" paragraph>
                We reserve the right to remove any content that violates these terms or is 
                otherwise objectionable. We are not responsible for user-generated content.
              </Typography>
            </Box>

            <Divider sx={{ my: 4 }} />

            {/* Section 4 - Payments */}
            <Box ref={paymentsRef} id="payments" sx={{ scrollMarginTop: 20, mb: 6 }}>
              <Typography variant="h5" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PaymentIcon /> 4. Payments
              </Typography>
              
              <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 2 }}>
                4.1 Subscription Services
              </Typography>
              <Typography variant="body2" paragraph>
                Some features may require payment. By subscribing to paid services, you agree 
                to pay all applicable fees. Fees are non-refundable except as required by law.
              </Typography>
              
              <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 3 }}>
                4.2 Billing and Renewal
              </Typography>
              <Typography variant="body2" paragraph>
                Subscriptions automatically renew unless cancelled at least 24 hours before 
                the renewal date. You are responsible for any taxes associated with your 
                use of our services.
              </Typography>
              
              <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 3 }}>
                4.3 Price Changes
              </Typography>
              <Typography variant="body2" paragraph>
                We may change our prices with 30 days' notice. Continued use after a price 
                change constitutes acceptance of the new pricing.
              </Typography>
            </Box>

            <Divider sx={{ my: 4 }} />

            {/* Section 5 - Termination */}
            <Box ref={terminationRef} id="termination" sx={{ scrollMarginTop: 20, mb: 6 }}>
              <Typography variant="h5" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ReportIcon /> 5. Termination
              </Typography>
              
              <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 2 }}>
                5.1 Termination by User
              </Typography>
              <Typography variant="body2" paragraph>
                You may stop using our services at any time. Certain provisions of these terms 
                will survive termination, including intellectual property rights, warranties, 
                and limitations of liability.
              </Typography>
              
              <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 3 }}>
                5.2 Termination by Company
              </Typography>
              <Typography variant="body2" paragraph>
                We may terminate or suspend your access immediately, without prior notice, 
                for conduct that we believe violates these terms or is harmful to other users, 
                us, or third parties.
              </Typography>
              
              <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 3 }}>
                5.3 Effect of Termination
              </Typography>
              <Typography variant="body2" paragraph>
                Upon termination, your right to use our services will cease immediately. We 
                may delete your account information and content in accordance with our 
                Privacy Policy.
              </Typography>
            </Box>

            <Divider sx={{ my: 4 }} />

            {/* Section 6 - Liability */}
            <Box ref={liabilityRef} id="liability" sx={{ scrollMarginTop: 20, mb: 6 }}>
              <Typography variant="h5" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SecurityIcon /> 6. Liability
              </Typography>
              
              <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 2 }}>
                6.1 Disclaimer of Warranties
              </Typography>
              <Typography variant="body2" paragraph>
                Our services are provided "as is" and "as available" without warranties of any 
                kind, either express or implied. We do not guarantee that our services will be 
                uninterrupted, timely, secure, or error-free.
              </Typography>
              
              <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 3 }}>
                6.2 Limitation of Liability
              </Typography>
              <Typography variant="body2" paragraph>
                To the maximum extent permitted by law, we shall not be liable for any indirect, 
                incidental, special, consequential, or punitive damages resulting from your use 
                or inability to use our services.
              </Typography>
              
              <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 3 }}>
                6.3 Indemnification
              </Typography>
              <Typography variant="body2" paragraph>
                You agree to indemnify and hold us harmless from any claims, damages, losses, 
                or expenses arising from your use of our services, your violation of these terms, 
                or your infringement of any third-party rights.
              </Typography>
            </Box>

            <Divider sx={{ my: 4 }} />

            {/* Section 7 - General Terms */}
            <Box ref={generalRef} id="general" sx={{ scrollMarginTop: 20, mb: 6 }}>
              <Typography variant="h5" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ArticleIcon /> 7. General Terms
              </Typography>
              
              <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 2 }}>
                7.1 Governing Law
              </Typography>
              <Typography variant="body2" paragraph>
                
              These terms shall be governed by the laws of Republic of Türkiye, without regard to its conflict of law provisions. However, local laws in your country of residence may provide you with additional consumer protection rights that cannot be waived by this agreement. For users located in the European Union, the laws of Ireland (or your country of residence) shall apply to the extent required by applicable law.


              </Typography>
              
              <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 3 }}>
                7.2 Dispute Resolution
              </Typography>
              

              <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                <Typography component="li" variant="body2" paragraph>
For users worldwide (excluding EU, Brazil, and other mandatory consumer protection jurisdictions): Any disputes arising from these terms will be resolved through binding arbitration in Republic of Türkiye, in accordance with the Istanbul Arbitration Association rules. The arbitration will be conducted in English. </Typography>
                <Typography component="li" variant="body2" paragraph>
For users in the European Union: You may bring any dispute before the competent court of your country of residence. Alternatively, you may use the European Commission's Online Dispute Resolution platform at https://ec.europa.eu/odr. </Typography>
                <Typography component="li" variant="body2" paragraph>
For all users: We encourage you to first contact us at info@otable.net to seek an amicable resolution. You may also have the right to bring claims in your local small claims court if applicable. </Typography>

                </Box>
              
            
                <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 3 }}>
                7.3 Class Action Waiver
              </Typography>
              <Typography variant="body2" paragraph>
              To the extent permitted by law, you agree to resolve any disputes on an individual basis and waive the right to participate in class actions, class arbitrations, or representative actions. This provision does not apply where prohibited by local laws (e.g., in the EU, Brazil, or other jurisdictions that prohibit class action waivers).
              </Typography>

              <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 3 }}>
                7.4 Severability
              </Typography>
              <Typography variant="body2" paragraph>
              If any provision of these terms is found to be invalid, illegal, or unenforceable in any jurisdiction, the remaining provisions will remain in full force and effect. The invalid provision shall be modified to the minimum extent necessary to make it enforceable while preserving the original intent.
              </Typography>
              
              <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 3 }}>
                7.5 Entire Agreement
              </Typography>
              <Typography variant="body2" paragraph>
              These terms constitute the entire agreement between you and us regarding our services, superseding any prior agreements. For users in the EU, this does not limit any statutory rights you may have as a consumer.
              </Typography>
              

              <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 3 }}>
                7.6 Language & Translations
              </Typography>
              <Typography variant="body2" paragraph>
              These terms are originally written in English. Translated versions are provided for convenience only. In the event of any conflict between the English version and a translated version, the English version shall prevail, except where local law requires otherwise.
              </Typography>

              
              <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 3 }}>
                7.7 Contact Information
              </Typography>
              
              <Typography variant="body2" paragraph>
              For questions about these terms, please contact us:  </Typography>
              <Box component="ul" sx={{ pl: 3, mb: 1 }}>
              <Typography variant="body2" > <strong>Email:</strong> info@otable.net.</Typography>
              <Typography variant="body2" > <strong>Address:</strong> Helixome Life Sciences and Information Technologies Ltd. Marmara Teknokent. Kocaeli, Türkiye</Typography>

              </Box>
            </Box>

            {/* Acceptance Section */}
            <Paper elevation={0} sx={{ 
              mt: 6, 
              p: 3, 
              border: 1, 
              borderColor: accepted ? 'success.main' : 'divider',
              borderRadius: 2,
              bgcolor: accepted ? alpha(theme.palette.success.main, 0.05) : 'transparent',
            }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleIcon color={accepted ? "success" : "disabled"} />
                Acceptance of Terms
              </Typography>
              
              <FormControlLabel
                control={
                  <Checkbox
                    checked={accepted}
                    onChange={(e) => setAccepted(e.target.checked)}
                    disabled={!isFullyRead}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2">
                    I have read and understood all 7 sections of the Terms of Service, and I agree to be bound by them.
                  </Typography>
                }
                sx={{ mt: 2 }}
              />
              
              {!isFullyRead && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Please read all sections before accepting. You have read {readSections.size} of 7 sections.
                </Alert>
              )}
              
              <Button variant="outlined" size="small" onClick={markAllAsRead} sx={{ mt: 2 }}>
                Mark all as read
              </Button>
            </Paper>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ 
        px: 3, 
        py: 2,
        borderTop: 1,
        borderColor: 'divider',
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            Version 1.0.0 | Effective June 1st, 2026
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button onClick={onClose} variant="outlined">
              Cancel
            </Button>
            <Button
              onClick={handleAccept}
              variant="contained"
              startIcon={<CheckCircleIcon />}
              disabled={!accepted && !isAccepted}
              color={isAccepted ? "error" : "primary"}
              sx={{ minWidth: 140 }}
            >
              {isAccepted ? 'Revoke' : 'Accept & Continue'}
            </Button>
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default TermsOfServiceDialog;